import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { context as baseqlContext } from '@adornis/baseql/server/context.js';
import i18next from 'i18next';
import { Exam } from '../../exam.js';
import { createPendingFileResolver } from '../file-intake.js';
import { handleImageFileUpload, isUserFacingUploadError, UserFacingUploadError } from '../file-upload.js';
import { deletePendingFileMutation } from '../pending-file.js';
import type { SolutionArtifact } from '../../extraction-progress.js';
import { dispatchJobs } from '../jobs/dispatch.js';
import { EXTRACTION_JOB_TYPE } from '../jobs/job.js';
import { startStructureExtractionMutation } from '../start-structure-extraction.js';
import { isStructureConfirmed } from '../tools/file-assignment.js';

/** Pending entries consumed by a successful upload are no longer pending — drop them. */
async function discardConsumedPendingFiles(examId: string, usedPendingIds: Set<string>, context: string) {
  for (const pendingId of usedPendingIds) {
    try {
      await deletePendingFileMutation(examId, pendingId)();
    } catch (err) {
      logger.warn({ err, pendingId }, `${context}: Pending-Datei konnte nicht gelöscht werden.`);
    }
  }
}

/** Pending files to process, by role. Values are filenames or `<pendingId>:<name>` tokens. */
export interface UploadedFilesByRole {
  submissions?: string[];
  masterSolution?: string;
  gradingScheme?: string;
  blankExam?: string;
}

/**
 * Processes pending files for one exam — the single path for both the first upload and every later
 * one. There is no "initial vs. re-upload" mode: the roles are stored the same way either way, and
 * the only state-dependent bit is whether the master solution / grading scheme can be extracted
 * immediately (the structure is confirmed) or is left for the structure confirmation to release.
 *
 * Every role is independent: an upload may contain only a grading scheme, or only a blank exam, and is
 * processed as far as the exam's state allows. Submissions are needed for grading, not for processing.
 *
 * Order matters: the non-submission roles are stored FIRST so they're on the exam before the
 * submission upload posts its split-confirmation message and (on a first upload) the extraction
 * pipeline picks the exam up.
 *
 * Failures of the non-submission roles are non-fatal — they're recorded on the exam state for the UI
 * and collected into a UPLOAD_PARTIAL_FAILED error thrown at the end, so one bad master solution
 * doesn't take the submissions down with it. A submission failure does propagate.
 */
export async function processUploadedFiles(
  filesByRole: UploadedFilesByRole,
  examId: string,
  conversationId: string,
): Promise<boolean> {
  if (!examId) {
    logger.error(`processUploadedFiles: examId fehlt.`);
    return false;
  }
  if (!conversationId) {
    logger.error(`processUploadedFiles: conversationId fehlt.`);
    return false;
  }

  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  if (!exam) {
    logger.error({ examId }, 'processUploadedFiles: Exam nicht gefunden.');
    return false;
  }

  // Extract against the confirmed structure right away, or leave it to the structure confirmation to
  // release. Uploading during the teacher's structure review must NOT extract yet: the tasks it would
  // match against are still being edited, and the confirmation runs both steps anyway.
  const extractImmediately = isStructureConfirmed(exam.examState);

  // The assignment panel sends `<pendingId>:<name>` tokens, so a re-uploaded file with an identical
  // name still maps to the pending entry the teacher actually assigned.
  const pending = createPendingFileResolver(exam.examState?.pendingFiles || [], { parsePendingIdTokens: true });

  const guestUserID = (baseqlContext as any).guestUserID as string | undefined;
  try {
    const nonBlockingWarnings: string[] = [];

    /** Stores one non-submission role. Returns whether it landed, so the caller can follow up. */
    const runOptionalUpload = async (
      label: string,
      uploadType: 'blankExam' | 'masterSolution' | 'gradingScheme',
      fileIds: string[],
    ): Promise<boolean> => {
      if (!fileIds.length) return false;
      const errorField =
        uploadType === 'masterSolution'
          ? 'examState.masterSolutionError'
          : uploadType === 'gradingScheme'
            ? 'examState.gradingSchemeError'
            : null;

      try {
        await handleImageFileUpload(examId, uploadType, fileIds, conversationId, guestUserID);
        if (errorField) {
          await examCollection.updateOne({ _id: examId }, { $unset: { [errorField]: '' } });
        }
        return true;
      } catch (err) {
        logger.error({ err, label }, 'processUploadedFiles: Fehler bei optionalem Upload. Fahre fort.');
        nonBlockingWarnings.push(
          isUserFacingUploadError(err)
            ? `${label}: ${err.userMessage}`
            : `${label}: ${i18next.t('upload.backgroundUploadFailedMessage', {
                defaultValue:
                  'Beim Verarbeiten der hochgeladenen Dateien ist ein Problem aufgetreten. Bitte prüfe deine Dateien und versuche den Upload erneut.',
              })}`,
        );

        // Track the error in ExamState for UI visualization and persist it to the database.
        if (errorField) {
          const errorCode = isUserFacingUploadError(err) ? err.code : 'UPLOAD_FAILED';
          await examCollection.updateOne({ _id: examId }, { $set: { [errorField]: errorCode } });
        }
        return false;
      }
    };

    const blankExamStored = await runOptionalUpload(
      'Leere Klausur',
      'blankExam',
      pending.resolveOne(filesByRole.blankExam),
    );

    // A blank exam is a complete structure source on its own — nothing has to be split or confirmed
    // first. So the structure extraction starts immediately and runs alongside the rest of this upload,
    // instead of waiting for submissions that may never come. The mutation is idempotent: it refuses to
    // start a second time while an extraction is running or finished.
    if (blankExamStored) {
      void startStructureExtractionMutation(examId, conversationId)().catch(err => {
        logger.error({ err, examId }, 'processUploadedFiles: Strukturerkennung konnte nicht gestartet werden.');
      });
    }

    const masterSolutionStored = await runOptionalUpload(
      'Musterlösung',
      'masterSolution',
      pending.resolveOne(filesByRole.masterSolution),
    );
    const gradingSchemeStored = await runOptionalUpload(
      'Bewertungsschema',
      'gradingScheme',
      pending.resolveOne(filesByRole.gradingScheme),
    );

    // One job per uploaded artifact. These used to be ONE serialised run for both, because each rewrote the
    // whole `tasks` array and two of them lost each other's writes; each now persists only its own fields
    // (`persistExtractedArtifact`) and holds its own lock, so they run at the same time.
    //
    // The other half of that old comment still stands and is NOT solved here: two artifacts finishing
    // separately means two re-grade dispatches and two announcements. `dispatchRegradeIfGrading` already
    // coalesces causes behind its settle window, which is what keeps the teacher from being told twice.
    const immediateSteps: SolutionArtifact[] = [];
    if (masterSolutionStored) immediateSteps.push('masterSolution');
    if (gradingSchemeStored) immediateSteps.push('gradingScheme');

    if (immediateSteps.length > 0 && extractImmediately) {
      // A re-upload has to re-run an extraction that already completed once, so `dedupeKey` (live rows only)
      // correctly lets a new row in, and it opens a genuinely NEW run rather than joining the first upload's
      // finished batch. Both of those are the extraction type's own `batch` declaration.
      for (const artifact of immediateSteps) {
        await dispatchJobs(EXTRACTION_JOB_TYPE[artifact], examId);
      }
    }

    const submissionFileIds = pending.resolveMany(filesByRole.submissions);
    if (submissionFileIds.length) {
      await handleImageFileUpload(examId, 'submissions', submissionFileIds, conversationId, guestUserID);
    }

    await discardConsumedPendingFiles(examId, pending.usedPendingIds, 'processUploadedFiles');

    logger.info({ examId, extractImmediately }, 'processUploadedFiles: Upload abgeschlossen.');

    if (nonBlockingWarnings.length > 0) {
      throw new UserFacingUploadError(
        'UPLOAD_PARTIAL_FAILED',
        nonBlockingWarnings.join('\n\n'),
        nonBlockingWarnings.join(' | '),
      );
    }

    return true;
  } catch (err) {
    logger.error({ err, examId }, 'processUploadedFiles: Fehler beim Verarbeiten der Dateien.');
    if (isUserFacingUploadError(err)) {
      throw err;
    }
    return false;
  }
}

interface AssignmentProposal {
  schuelerabgaben: string[];
  musterloesung?: string;
  bewertungsschema?: string;
  leereKlausur?: string;
}

export interface AssignmentProposalPayload {
  content: string;
  proposal: AssignmentProposal;
}

export function handleSendAssignmentConfirmationMessage(
  proposal: AssignmentProposal,
  messageContent?: string,
): Promise<AssignmentProposalPayload> {
  const fallbackText = i18next.t('upload.assignmentProposalFallback', {
    defaultValue: 'Bitte prüfe meinen Zuweisungsvorschlag und bestätige die Dateizuordnung.',
  });

  return Promise.resolve({
    content: typeof messageContent === 'string' && messageContent.trim() ? messageContent.trim() : fallbackText,
    proposal,
  });
}
